import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Check, CreditCard, ExternalLink, Sparkles } from "lucide-react";

const TIER_COLOR = {
  free: "bg-[#132A45] text-[#AAB7C7]",
  starter: "bg-[#C7F500]/15 text-[#C7F500]",
  club: "bg-[#F5B942]/15 text-[#F5B942]",
  trial: "bg-[#F5B942]/15 text-[#F5B942]",
  pro: "bg-purple-400/15 text-purple-300",
};

export default function PlanCard({ competition, onChange }) {
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/payments/config").then(({ data }) => setPlans(data.plans || []));
  }, []);

  const current = competition?.plan || { tier: "free", label: "Free", player_limit: 5 };
  const sub = competition?.subscription || {};
  const hasSub = !!sub.stripe_customer_id;
  const isComp = current.tier === "complimentary";

  if (isComp) {
    return (
      <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#F5B942]/30" data-testid="plan-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#F5B942]" />
            <h2 className="font-heading text-lg font-semibold">Plan &amp; Billing</h2>
          </div>
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#F5B942]/15 text-[#F5B942]" data-testid="current-tier-badge">
            Complimentary · unlimited players
          </span>
        </div>
        <p className="mt-3 text-sm text-[#AAB7C7]">
          This league is set to complimentary by the platform owner — no billing, no player caps.
        </p>
      </div>
    );
  }

  const startCheckout = async (lookup_key) => {
    setBusy(lookup_key); setErr("");
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        competition_id: competition.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      setBusy("");
    }
  };

  const openPortal = async () => {
    setBusy("portal"); setErr("");
    try {
      const { data } = await api.post("/payments/portal", {
        competition_id: competition.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      setBusy("");
    }
  };

  return (
    <div className="p-5 rounded-2xl bg-[#0D2038] border border-[#1F3A5C]" data-testid="plan-card">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-[#C7F500]" />
          <h2 className="font-heading text-lg font-semibold">Plan &amp; Billing</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${TIER_COLOR[current.tier] || TIER_COLOR.free}`} data-testid="current-tier-badge">
            {current.label} · up to {current.player_limit} players{current.trial_ends_at ? ` · ${Math.max(1, Math.ceil((new Date(current.trial_ends_at) - new Date()) / 86400000))} days left` : ""}
          </span>
          {hasSub && (
            <button onClick={openPortal} disabled={busy === "portal"} data-testid="manage-billing-btn"
              className="inline-flex items-center gap-1 text-xs text-[#C7F500] hover:underline">
              {busy === "portal" ? "Loading…" : <>Manage <ExternalLink className="w-3 h-3" /></>}
            </button>
          )}
        </div>
      </div>

      {sub.cancel_at_period_end && (
        <div className="mb-4 p-3 rounded-lg text-xs bg-[#F5B942]/10 text-[#F5B942] border border-[#F5B942]/30">
          Subscription cancels at the end of the current period. You can reactivate via Manage.
        </div>
      )}

      {err && <div className="mb-3 text-xs text-red-400" data-testid="plan-error">{err}</div>}

      <div className="grid md:grid-cols-3 gap-3">
        {plans.map((p) => {
          const isCurrent = sub.lookup_key === p.lookup_key && ["active", "trialing"].includes(sub.status);
          return (
            <div key={p.lookup_key}
              data-testid={`plan-${p.tier}`}
              className={`p-4 rounded-xl border ${isCurrent ? "border-[#C7F500] bg-[#C7F500]/5" : "border-[#1F3A5C] bg-[#071426]"}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-heading font-semibold">{p.label}</div>
                  <div className="text-xs text-[#AAB7C7]">Up to {p.player_limit} players</div>
                </div>
                {p.tier === "club" && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#F5B942]/20 text-[#F5B942]">POPULAR</span>}
              </div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-heading text-2xl font-bold">£{p.amount_gbp.toFixed(2)}</span>
                <span className="text-xs text-[#AAB7C7]">/ mo</span>
              </div>
              <ul className="mt-3 space-y-1 text-xs text-[#AAB7C7]">
                <li className="flex gap-1.5 items-start"><Check className="w-3.5 h-3.5 text-[#C7F500] mt-0.5" /> All leaderboards &amp; PDF exports</li>
                <li className="flex gap-1.5 items-start"><Check className="w-3.5 h-3.5 text-[#C7F500] mt-0.5" /> Joker &amp; custom monthly windows</li>
                <li className="flex gap-1.5 items-start"><Check className="w-3.5 h-3.5 text-[#C7F500] mt-0.5" /> Offline player management</li>
                {p.tier === "pro" && <li className="flex gap-1.5 items-start"><Sparkles className="w-3.5 h-3.5 text-purple-300 mt-0.5" /> Priority support</li>}
              </ul>
              <button
                onClick={() => startCheckout(p.lookup_key)}
                disabled={busy === p.lookup_key || isCurrent}
                data-testid={`select-plan-${p.tier}`}
                className={`mt-4 w-full py-2.5 rounded-lg font-semibold text-sm transition-colors ${
                  isCurrent
                    ? "bg-[#132A45] text-[#AAB7C7] cursor-default"
                    : "bg-[#C7F500] hover:bg-[#D9FF27] text-[#071426]"
                }`}
              >
                {isCurrent ? "Current plan" : busy === p.lookup_key ? "Redirecting…" : hasSub ? "Switch" : "Upgrade"}
              </button>
            </div>
          );
        })}
      </div>
      <p className="mt-3 text-[11px] text-[#8397AC]">
        Billed monthly in GBP via Stripe. VAT / company billing available at checkout. Cancel any time.
      </p>
    </div>
  );
}
